<template>
  <header class="header">
    <div class="header-inner">
      <router-link to="/" class="logo">
        <img src="@/assets/logo.png" alt="분양쟁이" class="logo-img" />
      </router-link>
      
      <nav class="nav">
        <router-link to="/" class="nav-link">홈</router-link>
        <router-link to="/search" class="nav-link">모든현장</router-link>
        <router-link to="/map" class="nav-link">지도별현장</router-link>
        <router-link to="/regions" class="nav-link">지역별현장</router-link>
        <router-link to="/sides" class="nav-link">사이드 모집</router-link>
        <!-- AI 추천 링크 -->
        <router-link to="/ai-chat" class="nav-link ai-link">
          <span>AI 추천</span>
          <span class="ai-dot"></span>
        </router-link>
        <router-link to="/events" class="nav-link">이벤트</router-link>
        
        <!-- 고객센터 드롭다운 -->
        <div class="more-menu" @mouseenter="openMoreMenu" @mouseleave="closeMoreMenu">
          <button class="nav-link more-btn" :class="{ active: isMoreMenuOpen }">
            <span>고객센터</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          <transition name="dropdown">
            <div v-if="isMoreMenuOpen" class="more-dropdown">
              <router-link to="/notices" class="more-item" @click="isMoreMenuOpen = false">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                공지사항
              </router-link>
              <router-link to="/inquiry" class="more-item" @click="isMoreMenuOpen = false">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                이용문의
              </router-link>
              <router-link to="/faq" class="more-item" @click="isMoreMenuOpen = false">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9 9a3 3 0 1 1 4 2.83c-.5.29-1 .87-1 1.5V15"/>
                  <circle cx="12" cy="18" r="0.5" fill="currentColor"/>
                </svg>
                FAQ
              </router-link>
              <router-link to="/products" class="more-item" @click="isMoreMenuOpen = false">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                  <line x1="7" y1="7" x2="7.01" y2="7"/>
                </svg>
                상품안내
              </router-link>
              <router-link to="/contact" class="more-item" @click="isMoreMenuOpen = false">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                광고문의
              </router-link>
            </div>
          </transition>
        </div>
      </nav>

      <div class="right-area">
        <!-- 검색 버튼 -->
        <button v-if="showSearchIcon" class="search-btn" @click="$emit('open-search')">
          <svg v-if="!isSearchOpen" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="6" y1="6" x2="18" y2="18"/>
            <line x1="6" y1="18" x2="18" y2="6"/>
          </svg>
          <span class="search-label">검색</span>
        </button>

        <div class="auth">
          <template v-if="!user">
            <router-link to="/login" class="auth-link">로그인</router-link>
            <router-link to="/register" class="btn-primary">회원가입</router-link>
          </template>
          <template v-else>
            <!-- 포인트 표시 -->
            <router-link to="/mypage/points" class="point-badge">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M10 13l1 1c1 1 2 1 3 0l2-2c1-1 1-2 0-3l-1-1"/>
                <path d="M14 11l-1-1c-1-1-2-1-3 0l-2 2c-1 1-1 2 0 3l1 1"/>
              </svg>
              <span>{{ formatPoints(userProfile?.points || 0) }}P</span>
            </router-link>

            <router-link to="/sides/create" class="btn-secondary">사이드등록</router-link>
            <router-link to="/jobs/create" class="btn-primary">공고등록</router-link>
            <div class="user-menu">
              <button class="user-btn" @click="isUserMenuOpen = !isUserMenuOpen">
                <span class="user-avatar">{{ userInitial }}</span>
              </button>
              <transition name="dropdown">
                <div v-if="isUserMenuOpen" class="dropdown-menu">
                  <div class="dropdown-header">
                    <span class="user-email">{{ user.email }}</span>
                  </div>
                  <router-link to="/mypage/store" class="dropdown-item" @click="isUserMenuOpen = false">
                    마이페이지
                  </router-link>
                  <router-link to="/mypage/points" class="dropdown-item" @click="isUserMenuOpen = false">
                    <span>포인트</span>
                    <span class="dropdown-point">{{ formatPoints(userProfile?.points || 0) }}P</span>
                  </router-link>
                  <router-link to="/mypage/jobs" class="dropdown-item" @click="isUserMenuOpen = false">
                    내 공고 관리
                  </router-link>
                  <router-link to="/mypage/sides" class="dropdown-item" @click="isUserMenuOpen = false">
                    내 사이드
                  </router-link>
                  <router-link to="/mypage/favorites" class="dropdown-item" @click="isUserMenuOpen = false">
                    관심 공고
                  </router-link>
                  <button class="dropdown-item logout" @click="handleLogout">
                    로그아웃
                  </button>
                </div>
              </transition>
            </div>
          </template>
        </div>
      </div>

      <div class="mobile-right">
        <!-- 모바일 AI 버튼 -->
        <router-link to="/ai-chat" class="mobile-ai-btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            <path d="M8 10h.01"/>
            <path d="M12 10h.01"/>
            <path d="M16 10h.01"/>
          </svg>
          <span class="mobile-ai-badge">AI</span>
        </router-link>
        
        <!-- 모바일 검색 버튼 -->
        <button v-if="showSearchIcon" class="mobile-search-btn" @click="$emit('open-search')">
          <svg v-if="!isSearchOpen" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="6" y1="6" x2="18" y2="18"/>
            <line x1="6" y1="18" x2="18" y2="6"/>
          </svg>
        </button>
        
        <button class="menu-btn" @click="openMobileMenu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <line x1="4" y1="7" x2="20" y2="7"/>
            <line x1="4" y1="12" x2="20" y2="12"/>
            <line x1="4" y1="17" x2="20" y2="17"/>
          </svg>
        </button>
      </div>
    </div>

    <div v-if="isUserMenuOpen" class="dropdown-overlay" @click="isUserMenuOpen = false"></div>

    <transition name="fade">
      <div v-if="isMenuOpen" class="overlay" @click="closeMobileMenu"></div>
    </transition>
    
    <transition name="slide">
      <div v-if="isMenuOpen" class="mobile-menu">
        <div class="mobile-menu-header">
          <img src="@/assets/logo.png" alt="분양쟁이" class="mobile-logo-img" />
          <button class="close-btn" @click="closeMobileMenu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="6" y1="6" x2="18" y2="18"/>
              <line x1="6" y1="18" x2="18" y2="6"/>
            </svg>
          </button>
        </div>

        <div v-if="user" class="mobile-user-info">
          <span class="mobile-user-avatar">{{ userInitial }}</span>
          <div class="mobile-user-detail">
            <span class="mobile-user-email">{{ user.email }}</span>
            <router-link to="/mypage/points" class="mobile-user-points" @click="closeMobileMenu">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v12"/>
                <path d="M8 10h8"/>
              </svg>
              {{ formatPoints(userProfile?.points || 0) }}P
            </router-link>
          </div>
        </div>
        
        <nav class="mobile-nav">
          <router-link to="/" class="mobile-link" @click="closeMobileMenu">홈</router-link>
          <router-link to="/search" class="mobile-link" @click="closeMobileMenu">모든현장</router-link>
          <router-link to="/map" class="mobile-link" @click="closeMobileMenu">지도별현장</router-link>
          <router-link to="/regions" class="mobile-link" @click="closeMobileMenu">지역별현장</router-link>
          <router-link to="/sides" class="mobile-link" @click="closeMobileMenu">사이드 모집</router-link>
          <router-link to="/events" class="mobile-link" @click="closeMobileMenu">이벤트</router-link>
          
          <div class="mobile-divider"></div>
          <div class="mobile-section-title">고객센터</div>
          <router-link to="/notices" class="mobile-link" @click="closeMobileMenu">공지사항</router-link>
          <router-link to="/inquiry" class="mobile-link" @click="closeMobileMenu">이용문의</router-link>
          <router-link to="/faq" class="mobile-link" @click="closeMobileMenu">FAQ</router-link>
          <router-link to="/products" class="mobile-link" @click="closeMobileMenu">상품안내</router-link>
          <router-link to="/contact" class="mobile-link" @click="closeMobileMenu">광고문의</router-link>
          
          <template v-if="user">
            <div class="mobile-divider"></div>
            <div class="mobile-section-title">마이페이지</div>
            <router-link to="/mypage/store" class="mobile-link" @click="closeMobileMenu">내 정보</router-link>
            <router-link to="/mypage/points" class="mobile-link mobile-link-points" @click="closeMobileMenu">
              <span>포인트</span>
              <span class="mobile-point-value">{{ formatPoints(userProfile?.points || 0) }}P</span>
            </router-link>
            <router-link to="/mypage/jobs" class="mobile-link" @click="closeMobileMenu">내 공고 관리</router-link>
            <router-link to="/mypage/sides" class="mobile-link" @click="closeMobileMenu">내 사이드</router-link>
            <router-link to="/mypage/favorites" class="mobile-link" @click="closeMobileMenu">관심 공고</router-link>
          </template>
        </nav>

        <div class="mobile-auth">
          <template v-if="!user">
            <router-link to="/login" class="mobile-btn-secondary" @click="closeMobileMenu">로그인</router-link>
            <router-link to="/register" class="mobile-btn-primary" @click="closeMobileMenu">회원가입</router-link>
          </template>
          <template v-else>
            <router-link to="/sides/create" class="mobile-btn-secondary" @click="closeMobileMenu">사이드등록</router-link>
            <router-link to="/jobs/create" class="mobile-btn-primary" @click="closeMobileMenu">공고등록</router-link>
            <button class="mobile-btn-logout" @click="handleLogout">로그아웃</button>
          </template>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'

export default {
  name: 'AppHeader',
  props: {
    showSearchIcon: {
      type: Boolean,
      default: false
    },
    isSearchOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['open-search', 'menu-open', 'menu-close'],
  setup(props, { emit }) {
    const router = useRouter()
    const { user, userProfile, logout } = useAuth()
    const isMenuOpen = ref(false)
    const isUserMenuOpen = ref(false)
    const isMoreMenuOpen = ref(false)
    let moreMenuTimer = null

    const openMoreMenu = () => {
      if (moreMenuTimer) {
        clearTimeout(moreMenuTimer)
        moreMenuTimer = null
      }
      isMoreMenuOpen.value = true
    }

    const closeMoreMenu = () => {
      moreMenuTimer = setTimeout(() => {
        isMoreMenuOpen.value = false
      }, 150)
    }

    const userInitial = computed(() => {
      if (!user.value) return ''
      const email = user.value.email
      return email ? email.charAt(0).toUpperCase() : 'U'
    })

    const formatPoints = (points) => {
      return new Intl.NumberFormat('ko-KR').format(points)
    }

    const openMobileMenu = () => {
      isMenuOpen.value = true
      emit('menu-open')
    }

    const closeMobileMenu = () => {
      isMenuOpen.value = false
      emit('menu-close')
    }

    const handleLogout = async () => {
      await logout()
      closeMobileMenu()
      isUserMenuOpen.value = false
      router.push('/')
    }

    return {
      user,
      userProfile,
      isMenuOpen,
      isUserMenuOpen,
      isMoreMenuOpen,
      userInitial,
      formatPoints,
      openMoreMenu,
      closeMoreMenu,
      openMobileMenu,
      closeMobileMenu,
      handleLogout
    }
  }
}
</script>

<style scoped>
.header {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  position: relative;
  z-index: 2;
}

.header-inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}

.logo {
  flex-shrink: 0;
}

.logo-img {
  height: 36px;
  width: auto;
  object-fit: contain;
}

/* 네비게이션 */
.nav {
  display: flex;
  gap: 28px;
  align-items: center;
  flex-shrink: 0;
}

.nav-link {
  font-size: 15px;
  font-weight: 500;
  color: #4e5968;
  transition: color 0.15s;
  white-space: nowrap;
  position: relative;
  padding: 4px 0;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: #191f28;
  transition: width 0.2s ease;
}

.nav-link:hover {
  color: #191f28;
}

.nav-link:hover::after {
  width: 100%;
}

.nav-link.router-link-exact-active {
  color: #191f28;
  font-weight: 600;
}

.nav-link.router-link-exact-active::after {
  width: 100%;
}

/* AI 추천 링크 */
.nav-link.ai-link {
  display: flex;
  align-items: center;
  gap: 6px;
}

.nav-link.ai-link::after {
  display: none;
}

.nav-link.ai-link span:first-child {
  position: relative;
}

.nav-link.ai-link span:first-child::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 0;
  width: 0;
  height: 2px;
  background: #667eea;
  transition: width 0.2s ease;
}

.nav-link.ai-link:hover span:first-child::after {
  width: 100%;
}

.nav-link.ai-link.router-link-exact-active span:first-child::after {
  width: 100%;
}

.ai-dot {
  width: 6px;
  height: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  margin-bottom: 10px;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

/* 더보기 메뉴 */
.more-menu {
  position: relative;
}

.more-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
}

.more-btn::after {
  display: none;
}

.more-btn svg {
  transition: transform 0.2s;
}

.more-btn.active svg {
  transform: rotate(180deg);
}

.more-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  width: 180px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  padding: 8px;
  z-index: 100;
}

/* 드롭다운 연결 영역 */
.more-menu::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  height: 10px;
}

.more-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 500;
  color: #4e5968;
  border-radius: 8px;
  transition: all 0.15s;
}

.more-item:hover {
  background: #f8f9fa;
  color: #191f28;
}

.more-item svg {
  flex-shrink: 0;
  color: #8b95a1;
}

.more-item:hover svg {
  color: #4e5968;
}

.right-area {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* 검색 버튼 스타일 */
.search-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #f2f4f6;
  border: 1px solid #e5e8eb;
  border-radius: 20px;
  color: #4e5968;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.search-btn:hover {
  background: #e5e8eb;
  border-color: #d1d6db;
  color: #191f28;
}

.search-btn svg {
  flex-shrink: 0;
}

.search-label {
  display: inline;
}

/* 포인트 뱃지 */
.point-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%);
  border: 1px solid #c9e2fc;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  color: #3182f6;
  transition: all 0.15s;
  white-space: nowrap;
}

.point-badge:hover {
  background: linear-gradient(135deg, #e5f1ff 0%, #d9ecfc 100%);
  border-color: #a8d4fb;
}

.point-badge svg {
  color: #3182f6;
  flex-shrink: 0;
}

.auth {
  display: flex;
  align-items: center;
  gap: 10px;
}

.auth-link {
  font-size: 15px;
  font-weight: 500;
  color: #4e5968;
  transition: color 0.15s;
  white-space: nowrap;
  padding: 8px 4px;
}

.auth-link:hover {
  color: #191f28;
}

.btn-primary {
  padding: 10px 18px;
  background: #191f28;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  transition: background 0.15s;
  white-space: nowrap;
}

.btn-primary:hover {
  background: #333d4b;
}

.btn-secondary {
  padding: 10px 18px;
  background: #f2f4f6;
  color: #191f28;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.15s;
  white-space: nowrap;
  border: 1px solid #e5e8eb;
}

.btn-secondary:hover {
  background: #e5e8eb;
  border-color: #d1d6db;
}

.user-menu {
  position: relative;
}

.user-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.user-avatar {
  width: 38px;
  height: 38px;
  background: #3182f6;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s;
}

.user-btn:hover .user-avatar {
  transform: scale(1.05);
}

.dropdown-overlay {
  position: fixed;
  inset: 0;
  z-index: 90;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 220px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  z-index: 100;
}

.dropdown-header {
  padding: 16px;
  border-bottom: 1px solid #f2f4f6;
}

.user-email {
  font-size: 14px;
  font-weight: 600;
  color: #191f28;
  word-break: break-all;
}

.dropdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #4e5968;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}

.dropdown-item:hover {
  background: #f9fafb;
  color: #191f28;
}

.dropdown-item.logout {
  color: #e03131;
}

.dropdown-point {
  font-weight: 600;
  color: #3182f6;
}

.mobile-right {
  display: none;
  align-items: center;
  gap: 8px;
}

/* 모바일 AI 버튼 */
.mobile-ai-btn {
  width: 40px;
  height: 40px;
  background: #f8f9fa;
  border: 1px solid #e5e8eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #191f28;
  position: relative;
  transition: all 0.2s ease;
}

.mobile-ai-btn:hover {
  background: #191f28;
  border-color: #191f28;
  color: #fff;
}

.mobile-ai-btn svg {
  width: 18px;
  height: 18px;
}

.mobile-ai-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  padding: 2px 5px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 8px;
  font-weight: 700;
  border-radius: 4px;
  border: 2px solid #fff;
}

/* 모바일 검색 버튼 */
.mobile-search-btn {
  width: 40px;
  height: 40px;
  background: #f2f4f6;
  border: 1px solid #e5e8eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4e5968;
  cursor: pointer;
  transition: all 0.15s;
}

.mobile-search-btn:hover {
  background: #e5e8eb;
  border-color: #d1d6db;
  color: #191f28;
}

.menu-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #191f28;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s;
}

.menu-btn:hover {
  background: #f2f4f6;
}

.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 200;
}

.mobile-menu {
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 320px;
  height: 100vh;
  background: #fff;
  z-index: 300;
  display: flex;
  flex-direction: column;
}

.mobile-menu-header {
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f2f4f6;
}

.mobile-logo-img {
  height: 28px;
  width: auto;
  object-fit: contain;
}

.close-btn {
  padding: 8px;
  background: none;
  border: none;
  color: #4e5968;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s;
}

.close-btn:hover {
  background: #f2f4f6;
}

.mobile-user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: #f9fafb;
}

.mobile-user-avatar {
  width: 44px;
  height: 44px;
  background: #3182f6;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-user-detail {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mobile-user-email {
  font-size: 14px;
  color: #4e5968;
  word-break: break-all;
}

.mobile-user-points {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #3182f6;
}

.mobile-nav {
  flex: 1;
  padding: 16px 0;
  overflow-y: auto;
}

.mobile-link {
  display: block;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 500;
  color: #191f28;
  transition: background 0.15s;
}

.mobile-link:hover {
  background: #f9fafb;
}

.mobile-link-points {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mobile-point-value {
  font-weight: 600;
  color: #3182f6;
}

.mobile-divider {
  height: 1px;
  background: #f2f4f6;
  margin: 12px 24px;
}

.mobile-section-title {
  padding: 8px 24px 4px;
  font-size: 12px;
  font-weight: 600;
  color: #8b95a1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mobile-auth {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-top: 1px solid #f2f4f6;
}

.mobile-btn-primary {
  padding: 16px;
  background: #191f28;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border-radius: 12px;
  text-align: center;
  border: none;
  cursor: pointer;
}

.mobile-btn-secondary {
  padding: 16px;
  background: #f2f4f6;
  color: #191f28;
  font-size: 15px;
  font-weight: 600;
  border-radius: 12px;
  text-align: center;
  border: 1px solid #e5e8eb;
  cursor: pointer;
}

.mobile-btn-logout {
  padding: 14px;
  background: transparent;
  color: #e03131;
  font-size: 14px;
  font-weight: 500;
  border-radius: 12px;
  text-align: center;
  border: none;
  cursor: pointer;
}

.mobile-btn-logout:hover {
  background: #fef2f2;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.15s;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* 1100px 이하에서 모바일 전환 */
@media (max-width: 1100px) {
  .header-inner {
    height: 60px;
    padding: 0 16px;
  }

  .nav,
  .right-area {
    display: none;
  }

  .mobile-right {
    display: flex;
  }

  .logo-img {
    height: 30px;
  }
}

/* 더 작은 화면에서 검색 라벨 숨김 */
@media (max-width: 1300px) and (min-width: 1101px) {
  .search-label {
    display: none;
  }
  
  .search-btn {
    padding: 10px;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    justify-content: center;
  }
}
</style>